import { useEffect, useState } from 'react';
import { env } from './env';

const HEALTH_TIMEOUT_MS = 3000;
const PING_INTERVAL_MS = 30_000;
let lastPing = 0;

async function healthPing(): Promise<boolean> {
  const base = env().VITE_API_BASE.replace(/\/+$/, '');
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
  try {
    const res = await fetch(`${base}/v1/health`, {
      method: 'HEAD',
      signal: controller.signal,
      credentials: 'include',
    });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(t);
  }
}

/**
 * React hook that returns true if we're online (`navigator.onLine` true AND
 * a recent health-ping succeeded). The ping confirms beyond navigator.onLine
 * because browsers sometimes report online on captive portals.
 *
 * - navigator.onLine === false → definitely offline (no ping)
 * - navigator.onLine === true → ping every 30s + on visibilitychange to confirm
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState<boolean>(navigator.onLine);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      if (!navigator.onLine) {
        if (!cancelled) setOnline(false);
        return;
      }
      // Throttle pings
      if (Date.now() - lastPing < PING_INTERVAL_MS) return;
      lastPing = Date.now();
      const reachable = await healthPing();
      if (!cancelled) setOnline(reachable);
    }

    function onOnline() { check(); }
    function onOffline() { setOnline(false); }
    function onVisible() { if (document.visibilityState === 'visible') check(); }

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);
    check();

    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return online;
}
